import type {
  SchedulingAvailabilityInfo,
  TradeInfo
} from '@carvana/merch-contracts';
import {
  defaultLoadableState,
  useEffectOnce,
  useLoadableState
} from '@carvana/merch-react-hooks';
import type { Result } from '@carvana/merch-result';
import {
  createContext,
  PropsWithChildren,
  useContext,
  useMemo,
  useState
} from 'react';
import type { LoadableState, PaymentTerm } from '~/contracts/common';
import BulkAvailabilityPayload from '~/contracts/common/BulkAvailabilityPayload';

type AvailabilityData = {
  soonestPickup: Result<SchedulingAvailabilityInfo>;
  soonestDelivery: Result<SchedulingAvailabilityInfo>;
};

export type PageAvailabilityData = Record<number, AvailabilityData>;

export type AvailabilityAndPriceContextType = {
  availabilityPayloads: LoadableState<BulkAvailabilityPayload[]>;
  carouselVehicleAvailability: LoadableState<BulkAvailabilityPayload[]>;
  pageAvailability: LoadableState<PageAvailabilityData>;
  pagePricing: LoadableState<Record<number, PaymentTerm>>;
  pageVehicleIds: number[] | null;
  recentVehicleAvailability: LoadableState<BulkAvailabilityPayload[]>;
  suggestedVehicleAvailability: LoadableState<BulkAvailabilityPayload[]>;
  tradeInfo: LoadableState<TradeInfo | undefined>;
};

const defaultContext: AvailabilityAndPriceContextType = {
  availabilityPayloads: defaultLoadableState<BulkAvailabilityPayload[]>([]),
  carouselVehicleAvailability: defaultLoadableState<BulkAvailabilityPayload[]>(
    []
  ),
  pageAvailability: defaultLoadableState<PageAvailabilityData>({}),
  pagePricing: defaultLoadableState<Record<number, PaymentTerm>>({}),
  pageVehicleIds: null,
  recentVehicleAvailability: defaultLoadableState<BulkAvailabilityPayload[]>(
    []
  ),
  suggestedVehicleAvailability: defaultLoadableState<BulkAvailabilityPayload[]>(
    []
  ),
  tradeInfo: defaultLoadableState<TradeInfo | undefined>(undefined)
};

const AvailabilityAndPriceContext =
  createContext<AvailabilityAndPriceContextType>(defaultContext);

export function AvailabilityAndPriceContextProvider({
  children
}: PropsWithChildren) {
  const availabilityPayloads = useLoadableState<BulkAvailabilityPayload[]>([]);
  const carouselVehicleAvailability = useLoadableState<
    BulkAvailabilityPayload[]
  >([]);
  const pageAvailability = useLoadableState<PageAvailabilityData>({});
  const pagePricing = useLoadableState<Record<number, PaymentTerm>>({});
  const [pageVehicleIds, setPageVehicleIds] = useState<number[] | null>(
    defaultContext.pageVehicleIds
  );
  const recentVehicleAvailability = useLoadableState<BulkAvailabilityPayload[]>(
    []
  );
  const suggestedVehicleAvailability = useLoadableState<
    BulkAvailabilityPayload[]
  >([]);
  const tradeInfo = useLoadableState<TradeInfo | undefined>(undefined);

  const value = useMemo<AvailabilityAndPriceContextType>(
    () => ({
      availabilityPayloads,
      carouselVehicleAvailability,
      pageAvailability,
      pagePricing,
      pageVehicleIds,
      recentVehicleAvailability,
      suggestedVehicleAvailability,
      tradeInfo
    }),
    [
      availabilityPayloads,
      carouselVehicleAvailability,
      pageAvailability,
      pagePricing,
      pageVehicleIds,
      recentVehicleAvailability,
      suggestedVehicleAvailability,
      tradeInfo
    ]
  );

  useEffectOnce(
    () => {
      setPageVehicleIds(
        availabilityPayloads.data.map(({ vehicleId }) => vehicleId)
      );
    },
    [availabilityPayloads.data],
    availabilityPayloads.isLoaded
  );

  return (
    <AvailabilityAndPriceContext.Provider value={value}>
      {children}
    </AvailabilityAndPriceContext.Provider>
  );
}

AvailabilityAndPriceContext.displayName = 'AvailabilityAndPriceContextProvider';

export function usePricingError(): boolean {
  const { pagePricing } = useContext(AvailabilityAndPriceContext);

  return pagePricing.error != null;
}

export default AvailabilityAndPriceContext;
